import { formatScoringSummaryEvent } from "../../../common/formatScoringSummaryEvent.hockey.ts";
import type { TeamNum } from "../../../common/types.ts";
import { PlayByPlayLoggerBase } from "../GameSim/PlayByPlayLoggerBase.ts";
import type { penaltyTypes } from "./penalties.ts";

type PlayByPlayEventInputScore =
	| {
			type: "goal";
			clock: number;
			t: TeamNum;
			names: [string] | [string, string] | [string, string, string];
			pids: [number] | [number, number] | [number, number, number];
			goalType: "ev" | "sh" | "pp" | "en";
			shotType: string;

			// undefined in ASG or old box scores
			totalGA:
				| [number]
				| [number, number]
				| [number, number, number]
				| undefined;
	  }
	| {
			type: "shootoutShot";
			clock: number;
			t: TeamNum;
			names: [string];
			goalieName: string;
			made: boolean;
			goalType: "pn";
			shotType: string;
	  };

type PlayByPlayEventInput =
	| {
			type: "quarter" | "overtime";
			quarter: number;
			clock: number;
	  }
	| {
			type: "gameOver";
			clock: number;
	  }
	| {
			type: "injury";
			clock: number;
			t: TeamNum;
			names: [string];
			injuredPID: number;
	  }
	| {
			type: "hit" | "faceoff";
			clock: number;
			t: TeamNum;
			names: [string, string];
	  }
	| {
			type:
				| "gv"
				| "tk"
				| "slapshot"
				| "wristshot"
				| "shot"
				| "reboundShot"
				| "block"
				| "miss"
				| "save"
				| "save-freeze"
				| "deflection";
			clock: number;
			t: TeamNum;
			names: [string];
	  }
	| PlayByPlayEventInputScore
	| {
			type: "offensiveLineChange" | "fullLineChange" | "defensiveLineChange";
			clock: number;
			t: TeamNum;
	  }
	| {
			type: "playersOnIce";
			t: TeamNum;
			pids: number[];
	  }
	| {
			type: "penalty";
			clock: number;
			t: TeamNum;
			names: [string];
			penaltyType: keyof typeof penaltyTypes;
			penaltyName: string;
			penaltyPID: number;
	  }
	| {
			type: "penaltyOver";
			clock: number;
			t: TeamNum;
			names: [string];
			penaltyPID: number;
	  }
	| {
			type: "pullGoalie";
			clock: number;
			t: TeamNum;
			name: string;
	  }
	| {
			type: "noPullGoalie";
			clock: number;
			t: TeamNum;
			name: string;
	  }
	| {
			type: "shootoutStart";
			rounds: number;
			clock: number;
	  }
	| {
			type: "shootoutTeam";
			t: TeamNum;
			names: [string];
			clock: number;
	  }
	| {
			type: "shootoutTie";
			clock: number;
	  };

export type PlayByPlayEventOutput = PlayByPlayEventInput & {
	quarter: number;
};

export type PlayByPlayEventScore = PlayByPlayEventInputScore & {
	quarter: number;
};

class HockeyPlayByPlayLogger extends PlayByPlayLoggerBase<PlayByPlayEventOutput> {
	scoringSummary: PlayByPlayEventScore[] = [];
	private quarter = 1;
	constructor(active: boolean) {
		super(active);
	}

	logEvent(event: PlayByPlayEventInput) {
		if (event.type === "quarter") {
			this.quarter = event.quarter;
		} else if (event.type === "overtime") {
			this.quarter += 1;
		}

		const event2: PlayByPlayEventOutput = {
			quarter: this.quarter,
			...event,
		};

		if (this.active) {
			this.playByPlay.push(event2);
		}

		const scoringSummaryEvent = formatScoringSummaryEvent(event2);
		if (scoringSummaryEvent) {
			this.scoringSummary.push(scoringSummaryEvent);
		}
	}
}

export default HockeyPlayByPlayLogger;
